/** The realtime recognizer takes 16-bit PCM, mono, 16 kHz. */
export const PCM_SAMPLE_RATE = 16000;
/** 100 ms per chunk, as ElevenLabs recommends (0.1–1 s). */
const CHUNK_SAMPLES = PCM_SAMPLE_RATE / 10;

// Runs on the audio thread: resamples the microphone to 16 kHz (linear
// interpolation; the context usually already runs at 16 kHz) and posts 100 ms
// Int16 chunks. Loaded from a Blob URL so the build needs no extra asset.
const WORKLET_SOURCE = `
class PcmCapture extends AudioWorkletProcessor {
    constructor() {
        super();
        this.ratio = sampleRate / ${PCM_SAMPLE_RATE};
        this.position = 0;
        this.chunk = new Int16Array(${CHUNK_SAMPLES});
        this.filled = 0;
    }
    process(inputs) {
        const input = inputs[0] && inputs[0][0];
        if (!input) return true;
        let position = this.position;
        while (position < input.length) {
            const index = Math.floor(position);
            const next = index + 1 < input.length ? input[index + 1] : input[index];
            const value = input[index] + (next - input[index]) * (position - index);
            this.chunk[this.filled++] = Math.max(-1, Math.min(1, value)) * 0x7fff;
            if (this.filled === this.chunk.length) {
                this.port.postMessage(this.chunk.buffer, [this.chunk.buffer]);
                this.chunk = new Int16Array(${CHUNK_SAMPLES});
                this.filled = 0;
            }
            position += this.ratio;
        }
        this.position = position - input.length;
        return true;
    }
}
registerProcessor('pcm-capture', PcmCapture);
`;

function createContext(): AudioContext {
    try {
        return new AudioContext({ sampleRate: PCM_SAMPLE_RATE });
    } catch {
        // A browser that cannot run a 16 kHz context: the worklet resamples instead.
        return new AudioContext();
    }
}

/**
 * The microphone as a stream of 100 ms PCM chunks. Echo cancellation is on, so
 * the app's own speech coming out of the car speakers is mostly not heard back.
 */
export class MicCapture {
    private readonly context: AudioContext;
    private stream: MediaStream | undefined;
    private closed = false;

    /** Create inside a click handler: the audio context may only start from a user gesture. */
    constructor(private readonly onChunk: (pcm: ArrayBuffer) => void) {
        this.context = createContext();
    }

    async open(deviceId?: string): Promise<void> {
        const audio: MediaTrackConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
        };
        if (deviceId !== undefined && deviceId !== '')
            audio.deviceId = { exact: deviceId };
        this.stream = await navigator.mediaDevices.getUserMedia({ audio });
        if (this.closed) {
            this.stream.getTracks().forEach(track => track.stop());
            return;
        }

        const moduleUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
        try {
            await this.context.audioWorklet.addModule(moduleUrl);
        } finally {
            URL.revokeObjectURL(moduleUrl);
        }

        const source = this.context.createMediaStreamSource(this.stream);
        const capture = new AudioWorkletNode(this.context, 'pcm-capture');
        capture.port.onmessage = event => this.onChunk(event.data as ArrayBuffer);
        // Kept connected to the output (silently) so the browser keeps pulling audio through it.
        const mute = this.context.createGain();
        mute.gain.value = 0;
        source.connect(capture);
        capture.connect(mute);
        mute.connect(this.context.destination);
        await this.context.resume();
    }

    close(): void {
        this.closed = true;
        this.stream?.getTracks().forEach(track => track.stop());
        void this.context.close().catch(() => undefined);
    }
}

/** Microphones the browser offers; labels are only filled in once access was granted. */
export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
    if (navigator.mediaDevices?.enumerateDevices === undefined)
        return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'audioinput');
}
